import React, { useState } from 'react';
import { Typography, Tooltip, Skeleton, Box } from '@mui/material';
import {
	ContentCopyRounded,
	CheckRounded,
	AddRounded,
	FolderOpenRounded,
	PeopleRounded,
} from '@mui/icons-material';
import { IDocenteMateria } from '../../../types/miRecurso';
import {
	MATERIA_COLORS,
	CardContainer,
	ColorStripe,
	CardBody,
	CardTopRow,
	MateriaAvatar,
	BadgesRow,
	ResourceCountBadge,
	AlumnoBadge,
	CardFooter,
	CodeRow,
	CodeText,
	CopyIconButton,
	AddMateriaCardContainer,
	AddIconCircle,
} from './styles';

interface MateriaCardProps {
	materia: IDocenteMateria;
	colorIndex: number;
	onSelect: () => void;
}

const MateriaCard: React.FC<MateriaCardProps> = ({
	materia,
	colorIndex,
	onSelect,
}) => {
	const [copied, setCopied] = useState(false);
	const colors = MATERIA_COLORS[colorIndex % MATERIA_COLORS.length];
	const initials = materia.nombre
		.split(' ')
		.filter((w) => w.length > 2)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase()
		.padEnd(2, materia.nombre[1]?.toUpperCase() ?? 'X');

	const handleCopyCode = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(materia.codigo).catch(() => {});
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<CardContainer onClick={onSelect} changesrequested={materia.hasChangesRequested}>
			<ColorStripe stripecolor={colors.stripe} />
			<CardBody>
				<CardTopRow>
					<MateriaAvatar bgcolor={colors.bg} accentcolor={colors.accent}>
						{initials}
					</MateriaAvatar>
					<BadgesRow>
						<ResourceCountBadge accentcolor={colors.accent}>
							<FolderOpenRounded style={{ fontSize: '0.9rem' }} />
							{materia.resourceCount} {materia.resourceCount === 1 ? 'recurso' : 'recursos'}
						</ResourceCountBadge>
						<AlumnoBadge>
							<PeopleRounded style={{ fontSize: '0.9rem' }} />
							{materia.alumnoCount} {materia.alumnoCount === 1 ? 'alumno' : 'alumnos'}
						</AlumnoBadge>
					</BadgesRow>
				</CardTopRow>

				<Typography variant='subtitle1' fontWeight={700} noWrap title={materia.nombre}>
					{materia.nombre}
				</Typography>

				<CardFooter>
					<CodeRow>
						<CodeText>{materia.codigo}</CodeText>
						<Tooltip title={copied ? '¡Copiado!' : 'Copiar código'} arrow>
							<CopyIconButton onClick={handleCopyCode}>
								{copied ? (
									<CheckRounded style={{ fontSize: '0.9rem' }} />
								) : (
									<ContentCopyRounded style={{ fontSize: '0.9rem' }} />
								)}
							</CopyIconButton>
						</Tooltip>
					</CodeRow>
				</CardFooter>
			</CardBody>
		</CardContainer>
	);
};

export const MateriaCardSkeleton: React.FC = () => (
	<CardContainer style={{ cursor: 'default', pointerEvents: 'none' }}>
		<Skeleton variant='rectangular' height={6} />
		<CardBody>
			<CardTopRow>
				<Skeleton variant='rounded' width={52} height={52} style={{ borderRadius: 12 }} />
				<Box style={{ display: 'flex', gap: 8 }}>
					<Skeleton variant='rounded' width={90} height={26} style={{ borderRadius: 20 }} />
					<Skeleton variant='rounded' width={80} height={26} style={{ borderRadius: 20 }} />
				</Box>
			</CardTopRow>
			<Skeleton variant='text' width='65%' height={28} />
			<Skeleton variant='text' width={110} height={20} />
		</CardBody>
	</CardContainer>
);

interface AddMateriaCardProps {
	onClick: () => void;
}

export const AddMateriaCard: React.FC<AddMateriaCardProps> = ({ onClick }) => (
	<AddMateriaCardContainer onClick={onClick}>
		<AddIconCircle>
			<AddRounded />
		</AddIconCircle>
		<Typography variant='body2' fontWeight={600} color='primary'>
			Agregar materia
		</Typography>
		<Typography variant='caption' color='text.secondary' align='center'>
			Registra una nueva materia para empezar a subir recursos
		</Typography>
	</AddMateriaCardContainer>
);

export default MateriaCard;
